import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  sub,
} from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import useRecognition from 'app/hooks/useRecognition';

import { TimeFilterOption, ViewMode } from './TheRoar.types';

const MIN_SEARCH_LENGTH = 3;

function resolveCreatedDates(
  timeOption: TimeFilterOption,
  customFrom: string,
  customTo: string
): { createdFrom?: string; createdTo?: string } {
  const now = new Date();

  switch (timeOption) {
    case 'thisWeek':
      return {
        createdFrom: startOfWeek(now, { weekStartsOn: 1 }).toISOString(),
        createdTo: endOfWeek(now, { weekStartsOn: 1 }).toISOString(),
      };
    case 'thisMonth':
      return {
        createdFrom: startOfMonth(now).toISOString(),
        createdTo: endOfMonth(now).toISOString(),
      };
    case 'last30Days':
      return {
        createdFrom: sub(now, { days: 30 }).toISOString(),
        createdTo: now.toISOString(),
      };
    case 'lastQuarter': {
      const lastQuarterDate = sub(now, { months: 3 });
      return {
        createdFrom: startOfQuarter(lastQuarterDate).toISOString(),
        createdTo: endOfQuarter(lastQuarterDate).toISOString(),
      };
    }
    case 'thisYear':
      return {
        createdFrom: startOfYear(now).toISOString(),
        createdTo: endOfYear(now).toISOString(),
      };
    case 'customRange':
      return {
        createdFrom: customFrom ? new Date(customFrom).toISOString() : undefined,
        createdTo: customTo ? new Date(customTo).toISOString() : undefined,
      };
    default:
      return {};
  }
}

function useTheRoar(companyId: string | undefined) {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedValueIds, setSelectedValueIds] = useState<string[]>([]);
  const [timeOption, setTimeOption] = useState<TimeFilterOption>('allTime');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { createdFrom, createdTo } = useMemo(
    () => resolveCreatedDates(timeOption, customFrom, customTo),
    [timeOption, customFrom, customTo]
  );

  const query = searchQuery.length >= MIN_SEARCH_LENGTH ? searchQuery : undefined;

  const infiniteQuery = useRecognition.useInfiniteListRecognitions(
    {
      companyId,
      groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
      valueIds: selectedValueIds.length > 0 ? selectedValueIds : undefined,
      createdFrom,
      createdTo,
      query,
    },
    !!companyId
  );

  const recognitions = useMemo(
    () => infiniteQuery.data?.pages.flatMap((p) => p.list) ?? [],
    [infiniteQuery.data]
  );

  const { mutate: hideRecognition } = useRecognition.useHideRecognition();
  const { mutate: unhideRecognition } = useRecognition.useUnhideRecognition();

  const [pendingHideId, setPendingHideId] = useState<string | null>(null);

  const handleHide = useCallback(
    (id: string) => {
      setPendingHideId(id);
      hideRecognition(id, { onSettled: () => setPendingHideId(null) });
    },
    [hideRecognition]
  );
  const handleUnhide = useCallback(
    (id: string) => {
      setPendingHideId(id);
      unhideRecognition(id, { onSettled: () => setPendingHideId(null) });
    },
    [unhideRecognition]
  );

  const handleTimeOptionChange = useCallback((option: TimeFilterOption) => {
    setTimeOption(option);
    if (option !== 'customRange') {
      setCustomFrom('');
      setCustomTo('');
    }
  }, []);

  return {
    viewMode,
    setViewMode,
    selectedGroupIds,
    setSelectedGroupIds,
    selectedValueIds,
    setSelectedValueIds,
    timeOption,
    customFrom,
    customTo,
    searchQuery,
    setSearchQuery,
    handleTimeOptionChange,
    setCustomFrom,
    setCustomTo,
    recognitions,
    infiniteQuery,
    handleHide,
    handleUnhide,
    pendingHideId,
  };
}

export default useTheRoar;
